/**
 * BelowBannerSafeArea — the content area under a full-width top banner
 * (PartnerAccessBanner, SnapshotViewBanner). The banner pads itself for the
 * status bar, so every screen below it must see a top inset of 0.
 *
 * On iOS and Android, `SafeAreaView` is a native view: it ignores
 * `SafeAreaInsetsContext` and pads by the insets of the nearest native
 * `SafeAreaProvider`. Overriding the context alone zeroed the top inset only
 * for `useSafeAreaInsets()` callers, and every `<SafeAreaView edges={['top']}>`
 * screen padded a second status-bar height below the banner. A provider laid
 * out under the banner measures its own insets (top 0, since it doesn't reach
 * the status bar), and both SafeAreaView and useSafeAreaInsets() read from it.
 * Its initial metrics already carry top 0, so the first frame doesn't flash
 * the full inset.
 *
 * On web, SafeAreaView reads the context, and the web provider reports
 * window-level insets wherever it sits, so a nested provider would bring the
 * inset back. Overriding the context is the right mechanism there.
 */

import React, { useMemo } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import {
  SafeAreaInsetsContext,
  SafeAreaProvider,
  useSafeAreaFrame,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';

interface Props {
  children: React.ReactNode;
}

export default function BelowBannerSafeArea({ children }: Props) {
  const insets = useSafeAreaInsets();
  const frame = useSafeAreaFrame();
  const childInsets = useMemo(() => ({ ...insets, top: 0 }), [insets]);

  if (Platform.OS === 'web') {
    return (
      <SafeAreaInsetsContext.Provider value={childInsets}>
        <View style={styles.fill}>{children}</View>
      </SafeAreaInsetsContext.Provider>
    );
  }

  return (
    <SafeAreaProvider initialMetrics={{ insets: childInsets, frame }}>
      {children}
    </SafeAreaProvider>
  );
}

const styles = StyleSheet.create({
  fill: { flex: 1 },
});
